import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  PaginationTotalStandalone,
} from "react-bootstrap-table2-paginator";
import axiosInstance from "Axiosinstance";
import { Col, Row } from "reactstrap";
import Loader from "components/Loader";
import { Redirect } from "react-router-dom";

const HISTORY_PAGE_SIZE = 10;

const REQUEST_TABS = {
  PENDING: 0,
  HISTORY: 1,
};

const getAdminStatusMessageColor = (adminApproved) => {
  if (adminApproved === "accepted") return "green";
  if (adminApproved === "rejected") return "red";
  return "#ed6c02";
};

const renderTableCell = (cell) => (
  <h5 className="font-size-14 mb-1">{cell ?? ""}</h5>
);

const renderColoredCell = (cell, color) => (
  <h5 className="font-size-14 mb-1">
    <span style={{ color, fontWeight: 600 }}>{cell ?? ""}</span>
  </h5>
);

const formatDateValue = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

const formatDateTimeValue = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const formatPrevNew = (previous, next) => {
  const prevText =
    previous === null || previous === undefined || previous === ""
      ? "—"
      : String(previous);
  const nextText =
    next === null || next === undefined || next === "" ? "—" : String(next);
  return (
    <h5 className="font-size-14 mb-1">
      <span style={{ color: "#6c757d" }}>{prevText}</span>
      <span style={{ margin: "0 6px" }}>→</span>
      <span style={{ fontWeight: 600 }}>{nextText}</span>
    </h5>
  );
};

const mapLogRows = (logs = [], page = 1) =>
  logs.map((log, index) => ({
    ...log,
    id: log._id?.toString?.() || log._id || log.id || `log-${page}-${index}`,
  }));

function ProductCategoryRequest() {
  const [loader, setLoader] = useState(false);
  const [requestTab, setRequestTab] = useState(REQUEST_TABS.PENDING);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearchInput, setHistorySearchInput] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("");
  const [reviewingId, setReviewingId] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingReview, setPendingReview] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperAdmin =
    user?.role === "superadmin" ||
    user?.activitiesAccess?.includes("All");

  const buildHistoryQuery = ({ page, search, status }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(HISTORY_PAGE_SIZE),
      purpose: "productcategoryupdate",
    });
    if (search) params.set("tag_number", search);
    if (status) params.set("status", status);
    return params.toString();
  };

  const fetchPendingRequests = async () => {
    try {
      setPendingLoading(true);
      const { data } = await axiosInstance.get(
        "/api/adminpendingtagupdaterequests"
      );
      setPendingRequests(mapLogRows(data?.logs));
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Unable to fetch pending product category update requests"
      );
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchHistoryLogs = async ({
    page = 1,
    search = historySearch,
    status = historyStatusFilter,
  } = {}) => {
    try {
      setHistoryLoading(true);
      const { data } = await axiosInstance.get(
        `/api/adminreviewedtagupdaterequests?${buildHistoryQuery({
          page,
          search,
          status,
        })}`
      );
      setHistoryLogs(mapLogRows(data?.logs, page));
      const total =
        typeof data?.pagination?.total === "number"
          ? data.pagination.total
          : (data?.logs || []).length;
      setHistoryTotal(total);
      setHistoryPage(data?.pagination?.page ?? page);
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Unable to fetch product category update request history"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPendingRequests();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHistorySearch(historySearchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [historySearchInput]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchHistoryLogs({
        page: 1,
        search: historySearch,
        status: historyStatusFilter,
      });
    }
  }, [isSuperAdmin, historySearch, historyStatusFilter]);

  const openConfirmModal = (row, decision) => {
    setPendingReview({ row, decision });
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setPendingReview(null);
  };

  const clearHistoryFilters = () => {
    setHistorySearchInput("");
    setHistorySearch("");
    setHistoryStatusFilter("");
  };

  const submitReview = async () => {
    if (!pendingReview) {
      return;
    }

    const { row, decision } = pendingReview;

    try {
      setReviewingId(row.id);
      setLoader(true);
      await axiosInstance.post("/api/reviewtagupdaterequest", {
        log_id: row.id,
        decision,
      });
      closeConfirmModal();

      await fetchPendingRequests();
      
      fetchHistoryLogs({ page: 1 });
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Unable to review product category update request"
      );
    } finally {
      setReviewingId(null);
      setLoader(false);
    }
  };

  const handleHistoryTableChange = (type, { page }) => {
    if (type === "pagination" && page !== historyPage) {
      fetchHistoryLogs({ page });
    }
  };

  const historyPageOptions = {
    page: historyPage,
    sizePerPage: HISTORY_PAGE_SIZE,
    totalSize: historyTotal || historyLogs.length,
    custom: true,
    hidePageListOnlyOnePage: false,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: true,
    showTotal: true,
    paginationTotalRenderer: (from, to, size) => (
      <span className="react-bootstrap-table-pagination-total">
        Showing {from} to {to} of {size} entries
      </span>
    ),
  };

  const pendingColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "product_category",
      text: "Product Category",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_product_category, cell),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell, row) => formatPrevNew(row.previous_company_name, cell),
    },
    {
      dataField: "validity_from_number",
      text: "Validity No.",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_validity_from_number, cell),
    },
    {
      dataField: "validity_from_unit",
      text: "Validity Unit",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_validity_from_unit, cell),
    },
    {
      dataField: "validity_start",
      text: "Validity From",
      formatter: (cell, row) =>
        formatPrevNew(
          formatDateValue(row.previous_validity_start),
          formatDateValue(cell)
        ),
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      formatter: (cell, row) =>
        formatPrevNew(
          formatDateValue(row.previous_expiry_date),
          formatDateValue(cell)
        ),
    },
    {
      dataField: "previous_isDeleted",
      text: "Was Deleted",
      formatter: (cell, row) =>
        renderTableCell(
          cell ? `Yes → No` : row.clear_is_deleted ? "No" : "No"
        ),
    },
    {
      dataField: "modified_by",
      text: "Modified By",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "modified_date",
      text: "Modified Date",
      formatter: (cell) => renderTableCell(formatDateTimeValue(cell)),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Tooltip title="Accept" arrow>
            <span>
              <IconButton
                aria-label="accept request"
                size="small"
                disabled={reviewingId === row.id}
                onClick={() => openConfirmModal(row, "accepted")}
                sx={{
                  color: "#fff",
                  bgcolor: "success.main",
                  "&:hover": { bgcolor: "success.dark" },
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reject" arrow>
            <span>
              <IconButton
                aria-label="reject request"
                size="small"
                disabled={reviewingId === row.id}
                onClick={() => openConfirmModal(row, "rejected")}
                sx={{
                  color: "#fff",
                  bgcolor: "error.main",
                  "&:hover": { bgcolor: "error.dark" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const historyColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "product_category",
      text: "Product Category",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_product_category, cell),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell, row) => formatPrevNew(row.previous_company_name, cell),
    },
    {
      dataField: "validity_from_number",
      text: "Validity No.",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_validity_from_number, cell),
    },
    {
      dataField: "validity_from_unit",
      text: "Validity Unit",
      formatter: (cell, row) =>
        formatPrevNew(row.previous_validity_from_unit, cell),
    },
    {
      dataField: "validity_start",
      text: "Validity From",
      formatter: (cell, row) =>
        formatPrevNew(
          formatDateValue(row.previous_validity_start),
          formatDateValue(cell)
        ),
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      formatter: (cell, row) =>
        formatPrevNew(
          formatDateValue(row.previous_expiry_date),
          formatDateValue(cell)
        ),
    },
    {
      dataField: "previous_isDeleted",
      text: "Was Deleted",
      formatter: (cell) => renderTableCell(cell ? "Yes → No" : "No"),
    },
    {
      dataField: "modified_by",
      text: "Requested By",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "modified_date",
      text: "Requested Date",
      formatter: (cell) => renderTableCell(formatDateTimeValue(cell)),
    },
    {
      dataField: "admin_approved_by",
      text: "Reviewed By",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "admin_approved_date",
      text: "Reviewed Date",
      formatter: (cell) => renderTableCell(formatDateTimeValue(cell)),
    },
    {
      dataField: "admin_approved",
      text: "Status",
      formatter: (cell) =>
        renderColoredCell(cell, getAdminStatusMessageColor(cell)),
    },
  ];

  const renderHistoryFilters = () => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        mb: 2,
      }}
    >
      <TextField
        size="small"
        label="Search Tag Number"
        value={historySearchInput}
        onChange={(e) => setHistorySearchInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        sx={{ m: "0 !important", width: { xs: "100%", sm: "220px" } }}
      />
      <TextField
        select
        size="small"
        label="Status"
        value={historyStatusFilter}
        onChange={(e) => setHistoryStatusFilter(e.target.value)}
        sx={{ m: "0 !important", width: { xs: "100%", sm: "180px" } }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="accepted">Accepted</MenuItem>
        <MenuItem value="rejected">Rejected</MenuItem>
      </TextField>
      <Button type="button" variant="outlined" onClick={clearHistoryFilters}>
        Clear
      </Button>
    </Box>
  );

  const renderPendingTable = () =>
    pendingLoading && !pendingRequests.length ? (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Loading pending requests...
      </div>
    ) : (
      <div className="table-responsive">
        <BootstrapTable
          keyField="id"
          data={pendingRequests}
          columns={pendingColumns}
          noDataIndication={() => "No pending product category update requests"}
        />
      </div>
    );

  const renderHistoryTable = () => (
    <>
      {renderHistoryFilters()}
      {historyLoading && !historyLogs.length ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          Loading request history...
        </div>
      ) : (
        <PaginationProvider
          pagination={paginationFactory(historyPageOptions)}
          keyField="id"
          columns={historyColumns}
          data={historyLogs}
        >
          {({ paginationProps, paginationTableProps }) => (
            <>
              <div className="table-responsive">
                <BootstrapTable
                  keyField="id"
                  data={historyLogs}
                  columns={historyColumns}
                  remote={{ pagination: true, filter: false, sort: false }}
                  onTableChange={handleHistoryTableChange}
                  noDataIndication={() =>
                    "No reviewed product category update requests"
                  }
                  {...paginationTableProps}
                />
              </div>
              <Row className="align-items-center mt-3 mb-2">
                <Col md="6" sm="6" className="react-bootstrap-table-pagination-total">
                  <PaginationTotalStandalone {...paginationProps} />
                </Col>
                <Col
                  md="6"
                  sm="6"
                  className="pagination pagination-rounded justify-content-end"
                >
                  <PaginationListStandalone {...paginationProps} />
                </Col>
              </Row>
            </>
          )}
        </PaginationProvider>
      )}
    </>
  );

  if (!isSuperAdmin) {
    return <Redirect to="/dashboard" />;
  }

  const pendingCount = pendingRequests.length;

  return (
    <div className="page-content">
      {loader ? <Loader /> : null}
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <div
          style={{
            marginBottom: "2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h1>Product Category Update Request</h1>
        </div>

        <Paper
          elevation={3}
          sx={{
            mt: 2,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "#f8f9fa",
            }}
          >
            <Tabs
              value={requestTab}
              onChange={(_, newValue) => setRequestTab(newValue)}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  minHeight: 56,
                  color: "text.secondary",
                },
                "& .Mui-selected": {
                  color: "primary.main",
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <span>Pending Requests</span>
                    <Box
                      component="span"
                      sx={{
                        color: "error.main",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                      }}
                    >
                      ({pendingCount})
                    </Box>
                  </Box>
                }
              />
              <Tab label="Request History" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: "white", minHeight: 280 }}>
            {requestTab === REQUEST_TABS.PENDING
              ? renderPendingTable()
              : renderHistoryTable()}
          </Box>
        </Paper>

        <Dialog open={confirmModalOpen} onClose={closeConfirmModal} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {pendingReview?.decision === "accepted"
              ? "Accept Product Category Update"
              : "Reject Product Category Update"}
          </DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 2 }}>
              {pendingReview?.decision === "accepted"
                ? "Are you sure you want to accept this product category update request?"
                : "Are you sure you want to reject this product category update request?"}
            </Typography>
            {pendingReview?.row ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                <Typography>
                  <strong>Tag Number:</strong> {pendingReview.row.tag_number}
                </Typography>
                <Typography>
                  <strong>Product Category:</strong>{" "}
                  {pendingReview.row.previous_product_category || "—"} →{" "}
                  {pendingReview.row.product_category || "—"}
                </Typography>
                <Typography>
                  <strong>Company Name:</strong>{" "}
                  {pendingReview.row.previous_company_name || "—"} →{" "}
                  {pendingReview.row.company_name || "—"}
                </Typography>
                <Typography>
                  <strong>Validity:</strong>{" "}
                  {pendingReview.row.previous_validity_from_number ?? "—"}{" "}
                  {pendingReview.row.previous_validity_from_unit || ""} →{" "}
                  {pendingReview.row.validity_from_number ?? "—"}{" "}
                  {pendingReview.row.validity_from_unit || ""}
                </Typography>
                <Typography>
                  <strong>Validity From:</strong>{" "}
                  {formatDateValue(pendingReview.row.previous_validity_start)} →{" "}
                  {formatDateValue(pendingReview.row.validity_start)}
                </Typography>
                <Typography>
                  <strong>Expiry Date:</strong>{" "}
                  {formatDateValue(pendingReview.row.previous_expiry_date)} →{" "}
                  {formatDateValue(pendingReview.row.expiry_date)}
                </Typography>
                <Typography>
                  <strong>Is Deleted:</strong>{" "}
                  {pendingReview.row.previous_isDeleted
                    ? "Yes → No (will be cleared on accept)"
                    : "No"}
                </Typography>
                <Typography>
                  <strong>Requested By:</strong> {pendingReview.row.modified_by}
                </Typography>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeConfirmModal} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              variant="contained"
              color={pendingReview?.decision === "accepted" ? "success" : "error"}
              disabled={!pendingReview || reviewingId === pendingReview?.row?.id}
            >
              {pendingReview?.decision === "accepted" ? "Accept" : "Reject"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
}

export default ProductCategoryRequest;
